/**
 * Shell icon set.
 *
 * Minimal inline SVG icons used by the app shell and shell-adjacent
 * route chrome. No runtime dependency on an icon library (D-043 forbids
 * adding `lucide-react` or similar without governance authorisation) —
 * each icon is a tiny hand-authored `<svg>` with a 24-unit viewBox sized
 * to `h-6 w-6` defaults. The icons are stroke-based: `baseSvgProps` pins
 * `fill="none"` and `stroke="currentColor"`, so token-backed text
 * utilities drive the stroke tint without per-icon colour coupling.
 *
 * Accessibility posture: icons default to `aria-hidden="true"` +
 * `focusable="false"` so assistive tech reads the surrounding label
 * (bottom-nav text, button `aria-label`) rather than the icon itself.
 * Consumers that want the icon to carry a meaning of its own (a
 * stand-alone icon-only button) override by passing
 * `aria-hidden={false}` and an explicit `aria-label`.
 *
 * The geometry is deliberately simple. These are navigation affordances,
 * not decorative illustrations — a clear silhouette at 24px reads as the
 * intended metaphor and survives the contrast requirements without
 * needing a raster asset.
 */

import type { JSX, SVGProps } from 'react';

export type IconProps = SVGProps<SVGSVGElement>;

const baseSvgProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
  xmlns: 'http://www.w3.org/2000/svg',
} as const;

/**
 * House silhouette — Koti (Home) nav entry.
 */
export function HomeIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Home</title>
      <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z" />
    </svg>
  );
}

/**
 * Envelope silhouette — Viestit (Messages) nav entry.
 */
export function MessagesIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Messages</title>
      <rect x={3} y={5} width={18} height={14} rx={2} />
      <path d="M3 7l9 7 9-7" />
    </svg>
  );
}

/**
 * Person outline — Omat tiedot (My info) nav entry.
 */
export function ProfileIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Profile</title>
      <circle cx={12} cy={8} r={4} />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/**
 * Hamburger bars — TopBar menu trigger.
 */
export function MenuIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Menu</title>
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </svg>
  );
}

/**
 * X — close affordance for the menu sheet (icon-only button). Replaces
 * the prior text-labelled "Sulje valikko" button so the dismiss control
 * matches the prototype's PWA drawer posture; the Finnish accessible
 * name is retained on the button via `aria-label`.
 */
export function CloseIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Close</title>
      <path d="M6 6l12 12" />
      <path d="M18 6l-12 12" />
    </svg>
  );
}

/**
 * Plus — the floating "Ota yhteyttä" contact entry (027 US6 fab-entry idiom):
 * the leading glyph on the pill that opens the compose wizard.
 */
export function PlusIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Plus</title>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/**
 * Briefcase — used in the CMS button-icon set (`components/cms/buttonIcons.tsx`).
 * Previously the Viesti Lakikaverille compose service-line chip; superseded
 * there by `ScaleIcon` at D-140 (proto icon alignment).
 */
export function BriefcaseIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Briefcase</title>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M3 12h18" />
    </svg>
  );
}

/**
 * Right-pointing chevron — trailing affordance for the Home quick-access
 * buttons (and any future "row that links somewhere" surface). Reads as
 * "this row leads onward" without committing to a destination.
 */
export function ChevronRightIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Chevron right</title>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/**
 * Pencil — the row-level edit cue revealed on a catalog row's hover/focus
 * (024-cms editor parity; the proto's row-leading edit affordance). Stroke
 * pencil (diagonal body + baseline) at the shared 24-unit viewBox.
 */
export function PencilIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Edit</title>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

/**
 * Crop frame — the "Rajaa kuvaa" affordance on a CMS image field (024-cms
 * page-editor parity / D4). Two overlapping L-brackets read as a crop marquee;
 * mirrors the proto's `Crop` glyph without adding `lucide-react` (D-043).
 */
export function CropIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Crop</title>
      <path d="M6 2v14a2 2 0 0 0 2 2h14" />
      <path d="M18 22V8a2 2 0 0 0-2-2H2" />
    </svg>
  );
}

/**
 * Down-pointing chevron — trailing affordance on an expanded
 * disclosure (017 / US1). Pairs with `ChevronRightIcon` to telegraph
 * the WAI Disclosure pattern: right-chevron when the submenu is
 * collapsed, down-chevron when expanded. Distinct path (not a
 * `rotate-90` of `ChevronRightIcon`) so the rendered glyph carries the
 * intended semantics on its own per research R-001 — AT users
 * inspecting the SVG title see "Chevron down" rather than a sideways
 * chevron rotated by CSS.
 */
export function ChevronDownIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Chevron down</title>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/**
 * Left-pointing arrow — back-affordance for the article-surface
 * sub-header. Stroke-based simple arrow (horizontal stem + chevron
 * head) at the same 24-unit viewBox as the rest of the icon set.
 */
export function BackArrowIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Back</title>
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

/** Circled information mark — supplementary explanatory copy. */
export function InfoIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Information</title>
      <circle cx={12} cy={12} r={9} />
      <path d="M12 11v6" />
      <circle cx={12} cy={7.5} r={0.75} fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Exclamation in a circle — the overdue-fee notice on Home (057). */
export function AlertCircleIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Attention</title>
      <circle cx={12} cy={12} r={9} />
      <path d="M12 7.5v5.5" />
      <circle cx={12} cy={16.5} r={0.75} fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Scales of justice — Lakikaveri (legal-companion) menu shortcut; also the
 * Lakikaveri compose service-line chip (027 D-140).
 */
export function ScaleIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Scale</title>
      <path d="M12 3v18" />
      <path d="M8 21h8" />
      <path d="M3 8h18" />
      <path d="M3 8l-2 5h6z" />
      <path d="M21 8l2 5h-6z" />
    </svg>
  );
}

/**
 * Person bust inside a ring — membership (jäsenpalvelu) compose service card.
 * The proto's step-2 glyph is lucide's `UserCircle`; hand-authored here as an
 * inline SVG because `lucide-react` is forbidden (D-043 / R-002).
 */
export function UserCircleIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>User circle</title>
      <circle cx={12} cy={12} r={10} />
      <circle cx={12} cy={10} r={3} />
      <path d="M6.5 19a6 6 0 0 1 11 0" />
    </svg>
  );
}

/**
 * Wrapped gift box — Jäsenyys ja edut (membership & benefits) menu shortcut.
 */
export function GiftIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Gift</title>
      <rect x={3} y={8} width={18} height={4} />
      <path d="M5 12v9h14v-9" />
      <path d="M12 8v13" />
      <path d="M12 8C9.5 8 8 6.5 8 5a2 2 0 0 1 4 0z" />
      <path d="M12 8c2.5 0 4-1.5 4-3a2 2 0 0 0-4 0z" />
    </svg>
  );
}

/**
 * Shield silhouette — Tapaturmavakuutus (accident insurance) deferred
 * menu entry. Insurance is the destination domain; shield is the
 * universal protective metaphor.
 */
export function ShieldIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Shield</title>
      <path d="M12 3l8 3v6c0 5-3 8-8 9-5-1-8-4-8-9V6z" />
    </svg>
  );
}

/**
 * Speech bubble — Ota yhteyttä (contact) deferred menu entry.
 */
export function ChatBubbleIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Chat bubble</title>
      <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z" />
    </svg>
  );
}

/**
 * Smartphone silhouette — OMA+ -palvelu launch-portal tile (the member
 * mobile application). Mirrors the proto's `Smartphone` affordance without
 * importing `lucide-react` (D-043 / R-002; the path is hand-authored, like
 * the rest of this set).
 */
export function SmartphoneIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Smartphone</title>
      <rect x={6} y={2} width={12} height={20} rx={2} />
      <path d="M10.5 18h3" />
    </svg>
  );
}

/**
 * Document with a folded corner + text lines — OMA+ sisällönhallinta
 * launch-portal tile (content authoring). Mirrors the proto's `FileText`
 * affordance without importing `lucide-react` (D-043 / R-002; hand-authored
 * path). Replaces the earlier cog/gear glyph, whose thin radiating spokes
 * read as a sun at the tile's 40px size and did not convey "content".
 */
export function DocumentIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Document</title>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6" />
      <path d="M9 17h6" />
    </svg>
  );
}

/**
 * Globe with equator + meridian — language picker in the menu sheet
 * (017/US5). Universal "change locale" metaphor consistent with the
 * stroke-based posture of the rest of the icon set. Matches the
 * prototype's globe affordance without importing `lucide-react`
 * (D-043 forbids vendor SDK icon imports; per research R-002 we hand-
 * author the path data instead).
 */
export function GlobeIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Globe</title>
      <circle cx={12} cy={12} r={9} />
      <path d="M3 12h18" />
      <path d="M12 3a13 13 0 0 1 0 18" />
      <path d="M12 3a13 13 0 0 0 0 18" />
    </svg>
  );
}

/**
 * Square with an arrow exiting through the upper-right corner —
 * external-link affordance. Used on CTAs that navigate off the app
 * (e.g. the membership-expired banner's "Liity uudelleen" link to
 * ytkpalvelut.fi). Hand-authored per D-043 / R-002 — no
 * `lucide-react` import.
 */
export function ExternalLinkIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>External link</title>
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
    </svg>
  );
}

/**
 * Door + arrow — sign-out menu row. Matches the prototype's "LogOut"
 * convention without copying its lucide path; the metaphor is the same
 * (arrow exits a bounded space).
 */
export function SignOutIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Sign out</title>
      <path d="M14 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
      <path d="M10 12h11" />
      <path d="M17 8l4 4-4 4" />
    </svg>
  );
}

/**
 * Arrow entering a bounded space — sign in (the mirror of SignOutIcon).
 * The MenuSheet's anonymous "Kirjaudu sisään" row uses it (items 2+3, D-130).
 */
export function SignInIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Sign in</title>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="M10 17l5-5-5-5" />
      <path d="M15 12H3" />
    </svg>
  );
}

/**
 * Overlapping squares — copy-to-clipboard action. Member profile card
 * copy button shows this icon, swapping to CheckIcon on success.
 */
export function CopyIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Copy</title>
      <rect x={9} y={9} width={13} height={13} rx={2} />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/**
 * Checkmark — success indicator. Member profile card copy button swaps
 * to this icon when clipboard.writeText() succeeds.
 */
export function CheckIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Success</title>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/**
 * Floppy-disk silhouette — Save-draft affordance on the CMS editor's
 * action row. Body + top slider opening + bottom paper-label inset
 * read as a 3.5" floppy at 16-24px. The inner label rect is filled
 * with `currentColor` so it reads as a solid paper-label inset
 * against the stroked outer body (the classic floppy rendering at
 * small sizes), and its `height=6` leaves a 1-unit gap before the
 * outer rect's bottom edge so the two stroke runs do not coincide
 * (avoids a double-weight bottom-line artifact at high DPI — the
 * gap keeps the strokes distinct and the fill makes the inset read
 * as solid rather than hollow). Matches the prototype's "Save"
 * affordance without copying its lucide path; the metaphor is the
 * same (the universal save = floppy disk). D-043 / R-002.
 */
export function SaveIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Save</title>
      <rect x={4} y={4} width={16} height={16} rx={1.5} />
      <path d="M8 4v5h8V4" />
      <rect x={7} y={13} width={10} height={6} fill="currentColor" />
    </svg>
  );
}

/**
 * Six-dot vertical grip — the universal drag-to-order HANDLE glyph on
 * the CMS inline ordering surface (024-cms / D-128). Two columns of three
 * filled dots read as "grab here to move" at 24px. Filled circles (not
 * strokes) because the dots are too small for a 2px stroke to render cleanly.
 * The handle button that wraps it carries the accessible name
 * (`cms.order.handle.label`), so this icon stays decorative
 * (`aria-hidden`). Hand-authored — no lucide path copied (D-043 / R-002).
 */
export function GripVerticalIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Reorder</title>
      <circle cx={9} cy={6} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={15} cy={6} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={9} cy={12} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={15} cy={12} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={9} cy={18} r={1.4} fill="currentColor" stroke="none" />
      <circle cx={15} cy={18} r={1.4} fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Three-dot vertical — the "more actions" MENU TRIGGER glyph on the CMS
 * catalog row (024-cms FR-064, the row's context menu). Deliberately
 * distinct from {@link GripVerticalIcon}, which sits on the SAME rows:
 * that one is TWO columns of three dots and means "grab here to move",
 * this one is ONE column of three and means "open the actions". One
 * column versus two is the whole difference at 24px, so the two must
 * not be swapped for each other.
 *
 * The trigger button that wraps it carries the accessible name, so the
 * icon stays decorative (`aria-hidden`). Filled circles rather than
 * strokes, for the same reason the grip uses them — a 2px stroke does
 * not render cleanly at this dot size. Hand-authored — no lucide path
 * copied (D-043 / R-002).
 */
export function MoreVerticalIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>More actions</title>
      <circle cx={12} cy={5} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={12} cy={12} r={1.6} fill="currentColor" stroke="none" />
      <circle cx={12} cy={19} r={1.6} fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Bar chart — OMA+ analytiikka launch-portal tile (025-admin-area US1).
 * An L-shaped axis with three ascending columns reads as "analytics /
 * metrics". The tile is shown disabled (FR-007 / P7); the icon inherits
 * the muted tint via `currentColor`. Hand-authored — D-043 / R-002.
 */
export function ChartBarIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Analytics</title>
      <path d="M4 4v16h16" />
      <path d="M8 16v-3" />
      <path d="M13 16v-6" />
      <path d="M18 16V8" />
    </svg>
  );
}

/**
 * Message-square speech bubble — OMA+ palautteet launch-portal tile
 * (025-admin-area US1). Mirrors the proto's `MessageSquare` affordance: a
 * plain rounded bubble with a bottom-left tail and no inner glyph (an
 * earlier version carried a small star, which the proto does not). The
 * contact-menu `ChatBubbleIcon` keeps its own distinct bubble path.
 * Hand-authored — D-043 / R-002.
 */
export function FeedbackIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Feedback</title>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/**
 * Up + down arrow pair — the sortable-column indicator on the OMA+
 * palautteet table (025-admin-area US5). Mirrors the proto's `ArrowUpDown`
 * affordance: two opposed arrows reading "this column can be sorted either
 * way". Geometry matches lucide's `arrow-up-down` path so the rendered glyph
 * reads identically without importing `lucide-react` (D-043 / R-002).
 */
export function ArrowUpDownIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Sort</title>
      <path d="M11 16l-4 4-4-4" />
      <path d="M7 20V4" />
      <path d="M13 8l4-4 4 4" />
      <path d="M17 4v16" />
    </svg>
  );
}

/**
 * Calendar grid — the "Valitse ajankohta" (custom range) filter affordance
 * on the palautteet dashboard. Two top binding rings, a rounded body, and a
 * header rule. Geometry matches lucide's `calendar` path; hand-authored per
 * D-043 / R-002 (no `lucide-react` import).
 */
export function CalendarIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Calendar</title>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x={3} y={4} width={18} height={18} rx={2} />
      <path d="M3 10h18" />
    </svg>
  );
}

/**
 * Tray + down arrow — the "Vie taulukkona" (export) affordance on the
 * palautteet dashboard. A downward arrow into an open tray reads as
 * "download this". Geometry matches lucide's `download` path; hand-authored
 * per D-043 / R-002 (no `lucide-react` import).
 */
export function DownloadIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Download</title>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}

/**
 * Tray + up arrow — the "Lataa" / "Vaihda tiedosto" affordance on the CMS
 * image upload control. The same open tray as `DownloadIcon` above with the
 * arrow reversed: out of the tray and upward reads as "send this file up".
 * Geometry matches lucide's `upload` path; hand-authored per D-043 / R-002
 * (no `lucide-react` import).
 *
 * Added rather than reusing `DownloadIcon` (owner-ruled 2026-09-03): a
 * downward arrow on a control that sends a file up misdescribes the action,
 * and this roster is ours to extend rather than a third-party set we are
 * constrained by.
 */
export function UploadIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Upload</title>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5" />
      <path d="M12 3V15" />
    </svg>
  );
}

/**
 * Billfold wallet with a press-stud pocket — Jäsenmaksu (membership fee)
 * entry point on the Omat tiedot hub (026-proto-convergence-v2 US3). Mirrors
 * the proto's `Wallet` affordance for the fee surface without importing
 * `lucide-react` (D-043 / R-002; hand-authored path). A rounded body with a
 * short top flap and a small stud circle reads as "wallet / money" at 24px.
 */
export function WalletIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Wallet</title>
      <path d="M3 7a2 2 0 0 1 2-2h12a1 1 0 0 1 1 1v1" />
      <path d="M3 7v10a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1H4" />
      <circle cx={16} cy={13} r={1.25} fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Map pin with a teardrop body + centre dot — the street-address field on the
 * contact-update takeover (026-proto-convergence-v2 US4). Mirrors the proto's
 * `MapPin` field affordance without importing `lucide-react` (D-043 / R-002;
 * hand-authored path). A teardrop that meets at a point with a hollow centre
 * reads as "location" at 24px.
 */
export function MapPinIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Map pin</title>
      <path d="M12 21c-4-4.5-7-7.8-7-11a7 7 0 0 1 14 0c0 3.2-3 6.5-7 11z" />
      <circle cx={12} cy={10} r={2.5} />
    </svg>
  );
}

/**
 * Mailbox on a post with the flag up — the postal-code field on the
 * contact-update takeover (026-proto-convergence-v2 US4). Mirrors the proto's
 * `Mailbox` field affordance without importing `lucide-react` (D-043 / R-002;
 * hand-authored path). A rounded-top box on a vertical post with a small raised
 * flag reads as "postal" at 24px. Distinct from `EnvelopeIcon` (email) and
 * `MessagesIcon` (the nav envelope).
 */
export function MailboxIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Mailbox</title>
      <path d="M4 11a4 4 0 0 1 8 0v6H6a2 2 0 0 1-2-2z" />
      <path d="M12 11h6a2 2 0 0 1 2 2v4h-8" />
      <path d="M9 17v3" />
      <path d="M16 11V8h3" />
    </svg>
  );
}

/**
 * Two-tower building with window rows — the city field on the contact-update
 * takeover (026-proto-convergence-v2 US4). Mirrors the proto's `Building2`
 * field affordance without importing `lucide-react` (D-043 / R-002;
 * hand-authored path). A taller block beside a shorter one with paired window
 * marks reads as "city / building" at 24px.
 */
export function BuildingIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Building</title>
      <path d="M9 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" />
      <path d="M9 9H4a1 1 0 0 0-1 1v11" />
      <path d="M13 8h2" />
      <path d="M13 12h2" />
      <path d="M13 16h2" />
      <path d="M6 13h1" />
      <path d="M6 17h1" />
    </svg>
  );
}

/**
 * Envelope with a folded flap — the email field on the contact-update takeover
 * (026-proto-convergence-v2 US4). Mirrors the proto's `Mail` field affordance
 * without importing `lucide-react` (D-043 / R-002; hand-authored path).
 * Authored as its own glyph rather than reusing `MessagesIcon` (the nav
 * envelope) so the field icon set stays self-contained, the same way
 * `ChatBubbleIcon` / `FeedbackIcon` keep distinct bubble paths.
 */
export function EnvelopeIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Email</title>
      <rect x={3} y={5} width={18} height={14} rx={2} />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

/**
 * Phone handset — the phone field on the contact-update takeover
 * (026-proto-convergence-v2 US4). Mirrors the proto's `Phone` field affordance
 * without importing `lucide-react` (D-043 / R-002; hand-authored path). A
 * single rounded handset silhouette reads as "phone" at 24px.
 */
export function PhoneIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Phone</title>
      <path d="M6 3h3l2 5-2 1.5a12 12 0 0 0 5.5 5.5L18 13l5 2v3a2 2 0 0 1-2 2A17 17 0 0 1 4 5a2 2 0 0 1 2-2z" />
    </svg>
  );
}

/**
 * Hash / number sign — the certificate member-number field (item-4 cert) and the
 * Laskun numero + Viitenumero rows on the Jäsenmaksu invoice card (item-4 fee).
 * Mirrors the proto's `Hash` field affordance without importing `lucide-react`
 * (D-043 / R-002; hand-authored path). Two slanted verticals crossed by two
 * horizontals read as "#/number" at 24px.
 */
export function HashIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Number</title>
      <path d="M4 9h16" />
      <path d="M4 15h16" />
      <path d="M10 3 8 21" />
      <path d="M16 3 14 21" />
    </svg>
  );
}

/**
 * Columned bank building (pediment + pillars + base) — the Tilinumero (account
 * number) row on the Jäsenmaksu invoice card. Mirrors the proto's `Landmark`
 * field affordance without importing `lucide-react` (D-043 / R-002;
 * hand-authored path). A classical bank facade reads as "account / bank".
 */
export function LandmarkIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Bank account</title>
      <path d="M3 21h18" />
      <path d="M4 10h16" />
      <path d="M12 3 20 8H4z" />
      <path d="M6 10v8" />
      <path d="M10 10v8" />
      <path d="M14 10v8" />
      <path d="M18 10v8" />
    </svg>
  );
}

/**
 * Euro sign (C-curve + two bars) — the Summa (amount) row on the Jäsenmaksu
 * invoice card. Mirrors the proto's `Euro` field affordance without importing
 * `lucide-react` (D-043 / R-002; hand-authored path).
 */
export function EuroIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Amount</title>
      <path d="M18 6a7 7 0 1 0 0 12" />
      <path d="M4 10h9" />
      <path d="M4 14h7" />
    </svg>
  );
}

/**
 * Payment card with a magnetic-stripe band — the Laskutustapa (billing method)
 * row on the Jäsenmaksu invoice card. Mirrors the proto's `CreditCard` field
 * affordance without importing `lucide-react` (D-043 / R-002; hand-authored
 * path).
 */
export function CreditCardIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Billing method</title>
      <rect x={2} y={5} width={20} height={14} rx={2} />
      <path d="M2 10h20" />
    </svg>
  );
}

/**
 * Trash can — the per-row archive ("Poista") affordance on the CMS
 * catalog (024-cms US6 / FR-024). Mirrors the proto's `Trash2` affordance
 * without importing `lucide-react` (D-043; hand-authored path): lidded
 * can with two inner strokes reads as "delete" at 24px. The omaplus
 * action is archive-not-delete (soft-delete only, FR-026) — the trash
 * metaphor is the proto's, kept for parity.
 */
export function TrashIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Trash</title>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

/**
 * Counter-clockwise restore arrow — the Arkisto drawer's "Palauta"
 * affordance (024-cms US6 / FR-024). Mirrors the proto's `RotateCcw`
 * without importing `lucide-react` (D-043; hand-authored path).
 */
export function RestoreIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Restore</title>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Home button (`sidebar_link`) icon set — US15 / D-122 (research D10)
// ---------------------------------------------------------------------------
// The ten icon identifiers the proto's authoring set carries that the shell
// did not already provide (the other nine map to existing components via
// `apps/web/src/components/cms/buttonIcons.tsx`). Hand-authored, stroke-based,
// same `baseSvgProps` posture as the rest of the set — no icon dependency
// (D-043). Drawn to read as the proto's metaphor at 24px.

/** Play triangle — Play button icon. */
export function PlayIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Play</title>
      <path d="M7 4v16l13-8z" />
    </svg>
  );
}

/** Mortarboard — GraduationCap (koulutus) button icon. */
export function GraduationCapIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>GraduationCap</title>
      <path d="M22 10 12 5 2 10l10 5 10-5z" />
      <path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
    </svg>
  );
}

/** Wrench — Wrench (työkalut) button icon. */
export function WrenchIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Wrench</title>
      <path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 0 5.4-5.4l-2.3 2.3-2.7-.7-.7-2.7z" />
    </svg>
  );
}

/** Calculator — Calculator (laskuri) button icon. */
export function CalculatorIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Calculator</title>
      <rect x={5} y={3} width={14} height={18} rx={2} />
      <path d="M8 7h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 19h.01" />
    </svg>
  );
}

/** Padlock — Lock (turva) button icon. */
export function LockIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Lock</title>
      <rect x={4} y={11} width={16} height={10} rx={2} />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/** Open book — BookOpen (opas) button icon. */
export function BookOpenIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>BookOpen</title>
      <path d="M12 6c-1.8-1.3-4.2-2-7-2v14c2.8 0 5.2.7 7 2 1.8-1.3 4.2-2 7-2V4c-2.8 0-5.2.7-7 2z" />
      <path d="M12 6v14" />
    </svg>
  );
}

/** Rosette — Award (ansiomerkki) button icon. */
export function AwardIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Award</title>
      <circle cx={12} cy={9} r={6} />
      <path d="M9 14.5 7.5 22 12 19l4.5 3-1.5-7.5" />
    </svg>
  );
}

/** Heart — Heart (suosikki) button icon. */
export function HeartIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Heart</title>
      <path d="M12 20s-7-4.4-9.3-8.7A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 9.3 5.3C19 15.6 12 20 12 20z" />
    </svg>
  );
}

/** Star — Star button icon. */
export function StarIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Star</title>
      <path d="m12 3 2.9 5.9 6.5.9-4.7 4.6 1.1 6.4L12 18.8 6.2 21.8l1.1-6.4L2.6 9.8l6.5-.9z" />
    </svg>
  );
}

/** Chain link — Link button icon. */
export function LinkIcon(props: IconProps): JSX.Element {
  return (
    <svg {...baseSvgProps} {...props}>
      <title>Link</title>
      <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
    </svg>
  );
}
